import { startOneTimePasscode } from '../helpers/start-one-time-passcode';
import { DomainInitPayload } from '@sxm-de/dev-kits/web-sdk/types';

import { getStatusForHandle } from '@sxm-de/dev-kits/web-sdk/domains/identity';

interface StartLoginFnPayload {
    handle: string;
    language: string;
}

export type StartLoginFn = (
    payload: StartLoginFnPayload,
) => Promise<StartLoginResults>;

export interface StartLoginResults {
    identityId: string;
    status: 'AwaitingOTP' | 'AwaitingAuthMethod';
    authMethodOptions: {
        type: 'PASSWORD' | 'PHONE' | 'EMAIL';
        hint?: string;
    }[];
}

type ContactOptions = {
    type: string;
    maskedValue: string;
};

export function initStartLoginFn({
    dependencies,
}: DomainInitPayload): StartLoginFn {
    const { appEventSdk, clientSdk } = dependencies;
    return async ({ handle, language }: StartLoginFnPayload) => {
        try {
            const userLoginStatus = await getStatusForHandle(
                dependencies,
                handle,
            );
            const results: StartLoginResults = {
                identityId: userLoginStatus.identityId,
                status: 'AwaitingAuthMethod',
                authMethodOptions: [],
            };

            appEventSdk.trackEvent({
                event: 'signin-verification-methods',
                flowInfo: {
                    verificationMethodsShown: [
                        ...(userLoginStatus.contactOptions.some(
                            (i: ContactOptions) => i.type === 'PHONE',
                        )
                            ? ['phone']
                            : []),
                        ...(userLoginStatus.contactOptions.some(
                            (i: ContactOptions) => i.type === 'EMAIL',
                        )
                            ? ['email']
                            : []),
                        ...(userLoginStatus.hasPassword ? ['password'] : []),
                    ],
                },
            });

            if (
                userLoginStatus.contactOptions.length === 1 &&
                userLoginStatus.contactOptions[0].type === 'EMAIL' &&
                !userLoginStatus.hasPassword
            ) {
                // immediately send OTP when only email is available
                await startOneTimePasscode(
                    {
                        identityId: userLoginStatus.identityId,
                        otpOption: 'email',
                        language,
                    },
                    dependencies,
                );
                results.authMethodOptions = [
                    {
                        type: 'EMAIL',
                        hint: userLoginStatus.contactOptions[0].maskedValue,
                    },
                ];
                results.status = 'AwaitingOTP';
            } else if (
                userLoginStatus.contactOptions.length === 1 &&
                userLoginStatus.contactOptions[0].type === 'PHONE' &&
                !userLoginStatus.hasPassword
            ) {
                // immediately send OTP when only phone is available
                await startOneTimePasscode(
                    {
                        identityId: userLoginStatus.identityId,
                        otpOption: 'phone',
                        language,
                    },
                    dependencies,
                );
                results.authMethodOptions = [
                    {
                        type: 'PHONE',
                        hint: userLoginStatus.contactOptions[0].maskedValue,
                    },
                ];
                results.status = 'AwaitingOTP';
            } else {
                // prompt user to choose method
                results.status = 'AwaitingAuthMethod';
                results.authMethodOptions = userLoginStatus.contactOptions.map(
                    (contactOption: {
                        type: 'EMAIL' | 'PHONE';
                        maskedValue: string;
                    }) => ({
                        type: contactOption.type,
                        hint: contactOption.maskedValue,
                    }),
                );
                if (userLoginStatus.hasPassword) {
                    results.authMethodOptions.push({ type: 'PASSWORD' });
                }
            }
            return results;
        } catch (error: any) {
            /**
             * TODO:
             * Log error to error logger (not analytics).
             */
            if (error?.message === 'INVALID_HANDLE') {
                throw error;
            } else {
                throw new Error('SYSTEM');
            }
        }
    };
}
